import { inject, Injectable, signal } from '@angular/core';
import { IFilterCriteria } from '../components/_primitives/IFilterCriteria';
import { DateRangePreset } from '../helpers/dateRangeHelpers';
import { FilterCriteriaDataRequest, FilterGroupDataRequest } from '../_DTOs/IStructuredData';
import { AuthentificationService } from './authentification.service';
import { MatwinsVariablesService } from './matwinsVariables.service';

export interface IFilterState {
  searchTerm: string;

  // Plage nommee retenue. Les bornes se recalculent a partir d'elle : conserver les dates
  // ferait qu' "Aujourd'hui" designerait encore le jour de la veille.
  dateRangePreset: DateRangePreset | null;

  // Bornes explicites, uniquement pour une periode libre, qui n'est pas recalculable.
  startDate: string | null;
  endDate: string | null;
  // Les identifiants suffisent a recocher les boutons ; les criteres, a rejouer le
  // filtrage sans dependre des options reconstruites par l'ecran.
  selectedOptionIds: string[];
  filterCriterias: IFilterCriteria[];
}

// Etat des filtres des listes, conserve en sessionStorage. La version fait partie de la
// cle : changer la forme de IFilterState invalide les etats precedents.
// Traduit des criteres en groupes : un groupe par champ, OR a l'interieur, AND entre eux.
export function BuildFilterGroups(criterias: IFilterCriteria[], entityName?: string): FilterGroupDataRequest[] {
  const groups: FilterGroupDataRequest[] = [];
  const valuesByField = new Map<string, string[]>();

  for (const criteria of criterias ?? []) {
    const values = valuesByField.get(criteria.fieldName) ?? [];
    values.push(`${criteria.fieldValue}`);
    valuesByField.set(criteria.fieldName, values);
  }

  for (const [fieldName, values] of valuesByField) {
    const groupCriteria: FilterCriteriaDataRequest[] = [{ fieldName, operator: 'in', values }];
    groups.push({ entityName, groupName: fieldName, criteria: groupCriteria });
  }

  return groups;
}

export function BuildFilterGroupsFromState(state: IFilterState | null, entityName?: string): FilterGroupDataRequest[] {
  return state?.filterCriterias ? BuildFilterGroups(state.filterCriterias, entityName) : [];
}

@Injectable({ providedIn: 'root' })
export class FilterStateService {
  private static readonly PREFIX = 'matwins-filters';
  private static readonly VERSION = 1;

  private varsService = inject(MatwinsVariablesService);
  private authService = inject(AuthentificationService);

  // Remonte aux listes deja montees qu'une remise a zero globale a eu lieu.
  readonly clearedAt = signal(0);

  // Listes de la vue en cours, pour que la remise a zero vise la bonne portee sans que
  // l'en-tete ait a connaitre les listes de chaque vue.
  readonly activeListKeys = signal<string[]>([]);

  // Compteur d'ecritures : sessionStorage n'est pas reactif, et une ecriture pendant la
  // detection rendait hasActiveFilters instable dans la meme passe (NG0100). Lire ce
  // signal dans le getter abonne les templates a chaque ecriture.
  private readonly revision = signal(0);

  // Seules ces valeurs font qu'une liste s'ecarte de son etat par defaut : recherche vide,
  // plage "aujourd'hui", aucun critere de categorie.
  private static isNonDefault(state: IFilterState | null): boolean {
    if (!state) return false;

    return !!state.searchTerm?.trim()
      || (state.dateRangePreset != null && state.dateRangePreset !== 'Today')
      || (state.selectedOptionIds?.length ?? 0) > 0;
  }

  // Vrai des qu'une liste de la vue courante a un filtre actif : c'est ce qui fait
  // apparaitre le bouton d'effacement, et disparaitre quand il n'a plus rien a effacer.
  get hasActiveFilters(): boolean {
    this.revision();
    return this.activeListKeys().some(key => FilterStateService.isNonDefault(this.get(key)));
  }

  clearActive(): void {
    this.clearFor(this.activeListKeys());
  }

  clearFor(listKeys: string[]): void {
    listKeys.forEach(key => this.clear(key));
    this.clearedAt.set(Date.now());
  }

  // L'utilisateur fait partie de la cle : deux personnes qui se succedent sur le meme
  // navigateur ne doivent pas heriter des filtres de la precedente.
  private storageKey(listKey: string): string {
    const user = this.authService.userName ?? '';

    return `${FilterStateService.PREFIX}-v${FilterStateService.VERSION}-${user}-${this.varsService.clientId ?? ''}-${listKey}`;
  }

  // Filtres restaures de plusieurs listes, reunis pour la requete primaire de la vue
  // d'ensemble : chaque groupe porte le nom de l'entite qu'il vise.
  filtersFor(listKeys: string[]): FilterGroupDataRequest[] {
    return listKeys.flatMap(listKey =>
      BuildFilterGroupsFromState(this.get(listKey), listKey.split('.').pop()));
  }

  get(listKey: string): IFilterState | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey(listKey));
      const state = raw ? JSON.parse(raw) as IFilterState : null;
      // Une entree ecrite par une version anterieure peut ne plus avoir la forme
      // attendue : on la ramene au contrat plutot que de laisser un `.trim()`
      // exploser plus bas sur un objet.
      if (state && typeof state === 'object') {
        if (typeof state.searchTerm !== 'string') state.searchTerm = '';
        if (typeof state.dateRangePreset !== 'string') state.dateRangePreset = null;
        if (typeof state.startDate !== 'string' || isNaN(Date.parse(state.startDate))) state.startDate = null;
        if (typeof state.endDate !== 'string' || isNaN(Date.parse(state.endDate))) state.endDate = null;
        if (!Array.isArray(state.selectedOptionIds)) state.selectedOptionIds = [];
        if (!Array.isArray(state.filterCriterias)) state.filterCriterias = [];
      }
      return state;
    } catch {
      return null;
    }
  }

  set(listKey: string, state: IFilterState): void {
    try {
      sessionStorage.setItem(this.storageKey(listKey), JSON.stringify(state));
    } catch {
      // quota depasse ou storage indisponible : l'etat des filtres n'est pas critique
    }
    this.revision.update(v => v + 1);
  }

  clear(listKey: string): void {
    try {
      sessionStorage.removeItem(this.storageKey(listKey));
    } catch {
      // idem
    }
    this.revision.update(v => v + 1);
  }

  clearAll(): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(FilterStateService.PREFIX)) keys.push(key);
      }
      keys.forEach(key => sessionStorage.removeItem(key));
    } catch {
      // idem
    }
    this.revision.update(v => v + 1);
    this.clearedAt.set(Date.now());
  }
}
